import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, first, takeUntil } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { AuthenticationService, DrawerService, SpearadAppService, SupplyPublisherService, VersionCheckService } from '../_services';
import { ApiService } from '../shared/_services/api.service';
import {
  CtrlNavigationContext,
  CtrlNavigationEntry,
  FeatureToggles,
  ISAMenuEntry,
  LastPath,
  PublisherEdit,
  PublisherInfo
} from '../shared/_models/models';
import {
  headerActionsConfig,
  iconDict,
  loggingOutConfig,
} from './_services/unruly-navigation.config';
import { cloneDeep, find, findIndex, flatten, isEqual } from 'lodash';
import { SharedStoreService } from '../shared/_services/shared-store.service';
import { LayoutService } from './_services/layout.service';
import { NavigationPermissions } from '../shared/_models/models';
import { UnrulyNavigationComponent } from './unruly-navigation/unruly-navigation.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.less']
})

export class LayoutComponent implements OnInit, OnDestroy {

  @ViewChild('spearadIframeEl') spearadIframeElRef: ElementRef;
  @ViewChild('unrulyNavigation') unrulyNavigationComponent: UnrulyNavigationComponent;
  year: number = new Date().getFullYear();
  search: string;
  publisherId: number;
  partnerId: number;
  publisherType: string;
  publisherTrafficSources: any[] = [];
  featureToggle: FeatureToggles;
  headerActionsConfigs: CtrlNavigationEntry[] = cloneDeep(headerActionsConfig);
  currentContext$: Observable<CtrlNavigationContext>;
  currentContext: CtrlNavigationContext = null;
  lastPath$: Observable<LastPath>;
  lastPath: LastPath;
  spearadToken$: Observable<{ spearadToken: string }>;
  spearadToken: string;
  initIframe$: Observable<any>;
  isInitIframeFailed = false;
  isCollapsed = false;
  isGlobalSearchOpen = false;
  isUpdateAvailable = false;
  isRouteValid = true;

  private unsubscribe$ = new Subject<void>();

  constructor(
    public router: Router,
    private activatedRoute: ActivatedRoute,
    public drawer: DrawerService,
    private notification: NzNotificationService,
    private api: ApiService,
    private supplyPublisherService: SupplyPublisherService,
    public vcs: VersionCheckService,
    private authService: AuthenticationService,
    private sharedStoreService: SharedStoreService,
    public spearadService: SpearadAppService,
    private layoutService: LayoutService,
    private cdr: ChangeDetectorRef
  ) {
    this.layoutService.init();
    this.vcs.isRefreshNeeded.subscribe(bool => this.onChangeIsUpdateAvailable(bool));
  }

  ngOnInit() {
    this.publisherId = this.authService.currentUserValue?.publisherId || null;
    this.partnerId = this.authService.currentUserValue?.partnerId || null;
    this.initRequests();
    this.initServiceSubscriptions();
    this.vcs.checkVersion(true);
    document.body.style.overflowX = 'hidden';
  }

  initRequests() {
    if (this.publisherId) {
      this.getPublisherTrafficSources();
    }
  }

  initServiceSubscriptions() {
    const publisher$: Observable<PublisherEdit> = this.publisherId ?
      this.supplyPublisherService.getPublisherById(this.publisherId).pipe(takeUntil(this.unsubscribe$)) : of(<PublisherEdit>{});
    combineLatest([
      publisher$,
      this.layoutService.featureToggle$
    ]).pipe(takeUntil(this.unsubscribe$)).subscribe(([publisherRes, featureToggleRes]) => {
      if (publisherRes && featureToggleRes) {
        this.featureToggle = featureToggleRes;
        this.publisherType = publisherRes?.publisherInfo?.publisherAccountType || null;
        if (this.publisherId) {
          this.handlePublisherPermissions(publisherRes.status, publisherRes.signupStep);
          this.updatePublisherSharedAssets(publisherRes.publisherInfo);
          this.generateSpearadToken();
        }
      }
    },
    () => {
      this.notification.error('Something went wrong', 'Error occurred');
    });

    // Subscribe to layout service state
    this.currentContext$ = this.layoutService.currentContext$;
    this.currentContext$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: CtrlNavigationContext) => {
      if (this.currentContext !== res) {
        this.currentContext = res;
      }
    });

    this.lastPath$ = this.layoutService.lastPath$;
    this.lastPath$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: LastPath) => {
      if (!isEqual(res, this.lastPath)) {
        this.lastPath = res;
      }
    });

    // Subscribe to UI state
    this.layoutService.isCollapsed$.pipe(takeUntil(this.unsubscribe$)).subscribe(collapsed => {
      this.isCollapsed = collapsed;
      this.cdr.detectChanges();
    });

    this.layoutService.isGlobalSearchOpen$.pipe(takeUntil(this.unsubscribe$)).subscribe(open => {
      this.isGlobalSearchOpen = open;
    });

    this.layoutService.isUpdateAvailable$.pipe(takeUntil(this.unsubscribe$)).subscribe(available => {
      this.isUpdateAvailable = available;
    });
  }

  updatePublisherSharedAssets(publisherInfo: PublisherInfo) {
    this.sharedStoreService.setSharedAssets('bizdevOwners', [publisherInfo?.bizdevOwner]);
    this.sharedStoreService.setSharedAssets('opsOwners', [publisherInfo?.operationsOwner]);
  }

  handlePublisherPermissions(publisherStatus: string, publisherSignupStep: string): void {
    const activeOrPaymentSubmitted = this.isPublisherActive(publisherStatus) || this.isPaymentSubmitted(publisherSignupStep);
    this.layoutService.updateNavigationPermissions({
      publisherIntegrationDetails: this.publisherType === 'SELF_SERVE' && activeOrPaymentSubmitted,
      publisherDeal: this.publisherType === 'SELF_SERVE' && activeOrPaymentSubmitted && this.authService.hasAccess('publisher.deal.show'),
      publisherReporting: activeOrPaymentSubmitted,
      publisherReportingDeals: this.authService.hasAccess('publisher.reporting.deals'),
      publisherReportingDetailed: this.authService.hasAccess('publisher.reporting.detailed'),
      publisherReportingDemand: this.authService.hasAccess('publisher.reporting.demand'),
    });
  }

  generateSpearadToken(): void {
    if (!this.layoutService.hasNavigationPermission('publisherDeal') || this.spearadToken$) {
      return;
    }
    this.spearadToken$ = this.api.generateSpearadToken().pipe(takeUntil(this.unsubscribe$), first());
    this.spearadToken$.subscribe(tokenRes => {
      this.spearadToken = tokenRes.spearadToken;
      this.initIframe();
    },
    () => {
      this.notification.error('Authentication Failed', 'Spearad login failed');
    });
  }


  initIframe(): void {
    if (this.layoutService.navConfigMap.spearad.length > 1) {
      return;
    }
    this.initIframe$ = this.spearadService.init(this.spearadIframeElRef, this.spearadToken);
    // refresh spearad token every 30 minutes = 30 * 60 * 1000 milliseconds
    const thirtyMinutes = 30 * 60 * 1000;
    const refreshInterval$ = this.spearadService.refreshTokenAtInterval(thirtyMinutes);
    refreshInterval$.pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.initIframe$.subscribe(
      res => {
        if (res.type === 'initialization') {
          const spearadNavConfigs = [ ...this.mapSpearadNavigationEntries(<ISAMenuEntry[]>res.menu),
            ...this.layoutService.navConfigMap.spearad ];
          this.layoutService.setNavConfigForContext('spearad', spearadNavConfigs);
          this.layoutService.updateFilteredNavContextList();
          if (this.currentContext === 'spearad') {
            this.navigateToLastPath();
          }
        }
      },
      _error => {
        this.isInitIframeFailed = true;
      }
    );
  }

  mapSpearadNavigationEntries(res: ISAMenuEntry[]): CtrlNavigationEntry[] {
    return res.map(parent => {
      const mappedParent: CtrlNavigationEntry = this.mapSearadNavigationEntry(parent);
      if (parent.children?.length) {
        mappedParent.children = parent.children.map(child => this.mapSearadNavigationEntry(child));
      }
      return mappedParent;
    });
  }

  mapSearadNavigationEntry(item): CtrlNavigationEntry {
    const itemPath = item.routerPath ? item.routerPath :
      item.path ? flatten([item.path.split('/')]) : null;
    return Object.assign(<CtrlNavigationEntry>{
      id: item.caption.toLowerCase().replace(' ', '-'),
      label: item.caption,
      path: itemPath,
      lottieFile: iconDict[item.icon] || iconDict.cogs,
      children: null,
      iframe: item.iframe !== undefined ? item.iframe : true,
    });
  }

  navigateToLastPath() {
    this.layoutService.navigateToLastPath();
  }

  getPublisherTrafficSources() {
    this.sharedStoreService.getTrafficSourcesByPublisherId(this.publisherId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (res) => {
        this.publisherTrafficSources = res.data.filter(pts => pts.status === 'ACTIVE' && pts.visible);
        const ptsNavigationEntries: CtrlNavigationEntry[] = this.publisherTrafficSources.map(pts => {
          const label = ['tagdirect', 'tagnetwork'].includes(pts.trafficSource.shortName) ? 'Video Tag' : pts.trafficSource.displayName;
          return Object.assign({
            id: `integrations-${pts.trafficSource.displayName.toLowerCase()}`,
            path: ['portal', 'integration-details', pts.pubTsId],
            label: label,
          });
        });
        const parent = find(this.layoutService.navConfigMap.ctrl, ['id', 'integrations']);
        parent.children = ptsNavigationEntries;
        this.layoutService.setNavConfigForContext('ctrl', this.layoutService.navConfigMap.ctrl);
        this.layoutService.updateFilteredNavContextList();
      }
    );
  }

  onItemAction(event: { actionId: string; payload?: { context?: CtrlNavigationContext } }) {
    const { actionId, payload } = event;
    switch (actionId) {
      case 'toggleSearch':
        this.onSearchClick();
        break;
      case 'accountAction':
        this.onAccountClick();
        break;
      case 'logoutAction':
        this.logout();
        break;
      case 'refreshAction':
        this.vcs.refreshPageIfNeeded();
        break;
      case 'navigateHome_ctrl':
        this.router.navigate(['portal']);
        break;
      case 'navigateHome_spearad':
        this.spearadService.navigate('home');
        this.router.navigate(['spearad', 'home']);
        break;
      default:
        break;
    }
  }

  onSearchClick() {
    switch (this.currentContext) {
      case 'ctrl':
      case 'supply':
      case 'exchange':
        this.layoutService.toggleGlobalSearch();
        break;
      case 'spearad':
        this.spearadService.openSearch();
        break;
      default:
        break;
    }
  }

  onAccountClick() {
    const accountItem = this.layoutService.navConfigMap[this.currentContext].find(item => item.id === 'account');
    if (accountItem) {
      if (accountItem.path) {
        this.router.navigate(accountItem.path);
      } else if (accountItem.children?.length) {
        this.router.navigate(accountItem.children[0].path);
      }
    }
  }

  logout() {
    this.setLoggingOutNavItem();
    const spearadLogout$ = this.initIframe$ ? this.spearadService.logout().pipe(catchError(() => EMPTY)) : EMPTY;
    const ctrlLogout$ = this.authService.logout();
    // TODO: fix logout between iframe init start and end
    spearadLogout$.subscribe(
      () => {},
      () => {},
      () => {
        this.layoutService.resetSpearadNavConfig();
        ctrlLogout$.subscribe(
          (_) => {},
          error => {
            if (error.status === 'fail') {
              this.notification.warning(error.data[0].error, error.data[0].messages);
            }
            this.setLogOutNavItem();
          }
        );
      });
  }

  setLogOutNavItem() {
    this.headerActionsConfigs = cloneDeep(headerActionsConfig);
  }

  setLoggingOutNavItem() {
    const loggingOutClone = cloneDeep(loggingOutConfig);
    const headerActionsClone = cloneDeep(headerActionsConfig);
    const logoutNavItemIdx = findIndex(headerActionsClone, ['id', 'logout']);
    headerActionsClone.splice(logoutNavItemIdx, 1, loggingOutClone);
    this.headerActionsConfigs = headerActionsClone;
  }

  isPaymentSubmitted(signupStep: string) {
    return signupStep.includes('PAYMENT');
  }

  isPublisherActive(status: string) {
    return status === 'ACTIVE';
  }

  onCollapseToggle() {
    this.layoutService.toggleCollapsed();
  }

  onChangeIsUpdateAvailable(bool) {
    this.layoutService.setUpdateAvailable(bool);
  }

  isBreadcrumbsVisible(): boolean {
    if (!this.isRouteValid) {
      return false;
    }
    if (['ctrl', 'supply', 'exchange', 'partner'].includes(this.currentContext)) {
      return true;
    }
    return false;
  }

  isRouterOutletVisible(): boolean {
    return ['ctrl', 'supply', 'exchange', 'partner'].includes(this.currentContext) && !this.layoutService.isFeatureToggleLoading;
  }

  isSpearadErrorVisible(): boolean {
    return this.currentContext === 'spearad' && this.isInitIframeFailed;
  }

  isIframeVisible(): boolean {
    return this.currentContext === 'spearad' && !this.isInitIframeFailed;
  }

  hasNavigationPermission(permission: keyof NavigationPermissions): boolean {
    return this.layoutService.hasNavigationPermission(permission);
  }

  ngOnDestroy(): void {
    document.body.style.overflowX = 'unset';
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.layoutService.destroy();
  }

}
